import React from 'react';
import { Linkedin, Mail, PhoneSignal, Whatsapp } from '@saleshandy/icons';
import { Permissions } from '../../../../../shared/utils/access-control/enums/permissions';
import hasPermission from '../../../../../shared/utils/access-control/has-permission';
import store from '../../../../../store';
import { getProspectAndEmailSentLimit } from '../../../../home/components/header/utils/helper';
import {
  isAgencyUser,
  getAgencyUserRole,
} from '../../../../../shared/utils/user-details';
import { UserRole } from '../../../../../shared/enums/user-roles';
import { ConversationType } from '../../../enums/prospect';
import { SequenceProgress, SequenceStepType } from '../../../../sequence/enums';
import TaskCheckBox from '../../../../../shared/svg/task-check-box';
import StatusBadge from '../../../../../shared/design-system/components/molecules/status-badge';
import { StatusBadgeVariant } from '../../../../../shared/design-system/components/molecules/status-badge/types';

export const getRestrictionPlanModalBodyContent = () => {
  const { totalProspectLimit } = getProspectAndEmailSentLimit();

  if (isAgencyUser()) {
    const msg1 = `Your current plans allows you to store  ${totalProspectLimit}  prospects.`;
    const msg2 =
      'Contact your admin and request to upgrade your subscription to continue adding more prospects';
    return [msg1, msg2];
  }

  const msg1 = `Your current plans allows you to store ${totalProspectLimit} prospects.`;
  const msg2 = hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)
    ? 'Please upgrade your subscription to add more prospects to your account.'
    : 'Contact your admin and request to upgrade your subscription to continue adding more prospects';

  return [msg1, msg2];
};

export const getRestrictionPlanWarningEmailBody = (): string => {
  const state = store.getState();
  const senderFirstName = state.home?.firstName;
  const { totalProspectLimit } = getProspectAndEmailSentLimit();

  if (isAgencyUser()) {
    const agencyUserRole = getAgencyUserRole();
    if (agencyUserRole && agencyUserRole === UserRole.AGENCYOWNER) {
      return `It looks like the Saleshandy account has reached the maximum prospect limit - ${totalProspectLimit}. It requires the upgrade to add more prospects into account. Please do the needful.%0D%0A %0D%0A Thanks,%0D%0A %0D%0A  ${senderFirstName}`;
    }
  }

  return `It looks like the Saleshandy account has reached the maximum prospect limit - ${totalProspectLimit}. It requires the upgrade to add more prospects into account. Please do the needful.%0D%0A %0D%0A Thanks,%0D%0A %0D%0A  ${senderFirstName}`;
};

export const getEmailSendingRestrictionModalContent = () => {
  const { totalEmailSentLimit } = getProspectAndEmailSentLimit();

  const msg1 = `Your current plan allows you to send ${totalEmailSentLimit} emails in a month.`;
  const msg2 = hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)
    ? 'Please upgrade your subscription to resume your email-sending service for your account.'
    : 'Contact your admin and request to upgrade your subscription to resume email-sending service for your account.';

  return [msg1, msg2];
};

export const getOverLimitConnectedEmailErrorMsg = () => {
  const msg1 =
    'Your current plan allows you to connect only 10 email for each sequence.';
  const msg2 = hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)
    ? 'Please upgrade your subscription to resume your sequence or remove extra emails from this sequence.'
    : 'Contact your admin and request to upgrade your subscription to resume this sequence or remove extra emails from this sequence.';

  return [msg1, msg2];
};

export const getConversationTypeLabel = (type: ConversationType): string => {
  let label = '';

  switch (type) {
    case ConversationType.External:
      label = 'External';
      break;
    case ConversationType.System:
      label = 'Sequence';
      break;
    default:
      label = 'All';
  }

  return label;
};

export const getSequenceStatus = (progress) => {
  if (progress === SequenceProgress.Active) {
    return <StatusBadge label="Active" variant={StatusBadgeVariant.SUCCESS} />;
  }
  if (progress === SequenceProgress.Pause) {
    return <StatusBadge label="Paused" variant={StatusBadgeVariant.DANGER} />;
  }
  if (progress === SequenceProgress.Draft) {
    return <StatusBadge label="Draft" variant={StatusBadgeVariant.SECONDARY} />;
  }
  return '';
};

export const getSequenceStepType = (type) => {
  if (
    type === SequenceStepType.LinkedInConnectionRequest ||
    type === SequenceStepType.LinkedInMessage ||
    type === SequenceStepType.LinkedInInMail ||
    type === SequenceStepType.LinkedInViewProfile ||
    type === SequenceStepType.LinkedInPostInteraction
  ) {
    return (
      <Linkedin
        width={14}
        height={14}
        className="blue-txt-29 mr-1 d-flex align-items-center"
      />
    );
  }
  if (
    type === SequenceStepType.WhatsAppMessage ||
    type === SequenceStepType.WhatsAppVoiceMessage ||
    type === SequenceStepType.WhatsAppVoiceCall
  ) {
    return (
      <Whatsapp
        width={14}
        height={14}
        className="green-txt-26 mr-1 d-flex align-items-center"
      />
    );
  }
  if (
    type === SequenceStepType.Call ||
    type === SequenceStepType.Introduction ||
    type === SequenceStepType.Demo ||
    type === SequenceStepType.FollowUp ||
    type === SequenceStepType.Reminder ||
    type === SequenceStepType.Other
  ) {
    return (
      <PhoneSignal
        width={14}
        height={14}
        className="purple-txt-16 mr-1 d-flex align-items-center"
      />
    );
  }
  if (type === SequenceStepType.Email) {
    return (
      <Mail
        width={14}
        height={14}
        className="blue-txt-24 mr-1 d-flex align-items-center"
      />
    );
  }
  if (type === SequenceStepType.Custom) {
    return (
      <TaskCheckBox
        width={14}
        height={14}
        className="orange-txt-21 mr-1 d-flex align-items-center"
      />
    );
  }
  return (
    <Mail
      width={14}
      height={14}
      className="gray-txt-15 mr-1 d-flex align-items-center"
    />
  );
};

export const ADD_SEQUENCE_ERROR_MAP: Record<string, string> = {
  'Bad prospect': 'Bad Prospect cannot be added to the sequence.',
  'Bounced prospect': 'Hard Bounced Prospect cannot be added to the sequence.',
  'Unsubscribed prospect':
    'Unsubscribed Prospect cannot be added to the sequence.',
  'Admin Setting declined-Prospect cannot exist in multiple sequences':
    'Admin Setting declined-Prospect cannot exist in multiple sequences',
  'Prospect already present in same step in same sequence':
    'Prospect is already present in same step in same sequence',
  'Prospect already present in another step and replied in same sequence':
    'Prospect already present in another step and replied in same sequence',
};
